/**
 * SceneGalleryGrid — compact grid view of a storyboard's scenes.
 *
 * Each cell shows:
 *   - The scene image (16:9 aspect) with a colored border:
 *     GREEN  → the scene has an `approved_version_id`
 *     YELLOW → not yet approved (still needs review)
 *   - A "Scene N" label beneath the image
 *   - A prominent "✓ Approve" button that toggles approval on the
 *     scene's LATEST preview version. Disabled with an explanation
 *     if the scene doesn't have a preview rendered yet.
 *   - Clicking the image opens the full detail editor for that
 *     scene (via `onOpenScene(scene_id)` — the parent scrolls the
 *     detail view to that anchor and hides the gallery).
 *
 * Read-only for admin impersonation (`readOnly=true` hides the
 * approve buttons and disables clicks).
 */
import { useState } from "react";
import { toast } from "sonner";
import { Check, ImageIcon, Loader2 } from "lucide-react";
import { approveSceneVersion } from "@/lib/api";
import { imgUrl } from "@/lib/imgUrl";

function statusBadge(scene) {
  const ps = scene.preview_status;
  if (ps === "running")
    return { label: "rendering", cls: "text-yellow-300 bg-yellow-500/10 border-yellow-500/40" };
  if (ps === "failed")
    return { label: "render failed", cls: "text-red-300 bg-red-500/10 border-red-500/40" };
  if (scene.approved_version_id)
    return { label: "approved", cls: "text-emerald-300 bg-emerald-500/10 border-emerald-500/40" };
  if ((scene.video_versions || []).length > 0)
    return { label: "ready to review", cls: "text-yellow-200 bg-yellow-400/10 border-yellow-400/40" };
  return { label: "needs preview", cls: "text-zinc-400 bg-zinc-500/10 border-zinc-500/40" };
}

function GalleryCard({ scene, idx, storyboardId, onOpen, onChanged, readOnly }) {
  const [busy, setBusy] = useState(false);
  const approved = !!(scene.approved_version_id || "").trim();
  const latestId = (scene.latest_version_id || "").trim();
  const versions = scene.video_versions || [];
  const hasAnyVersion = versions.length > 0;
  // If the scene has a `latest_version_id` we approve that; else
  // fall back to the newest version in the array (first item).
  const approveTargetId = latestId || versions[0]?.version_id || "";
  const canApprove = !readOnly && !!approveTargetId;

  const badge = statusBadge(scene);
  const borderColor = approved
    ? "border-emerald-400 ring-2 ring-emerald-400/40"
    : "border-yellow-400 ring-2 ring-yellow-400/30";
  const imgSrc = scene.image_url ? imgUrl(scene.image_url) : "";

  const toggleApprove = async (e) => {
    e.stopPropagation();
    if (!canApprove || busy) return;
    setBusy(true);
    try {
      // If already approved, clicking toggles OFF (backend detects
      // same version_id). If not, it approves. Either way we get a
      // fresh doc via onChanged().
      await approveSceneVersion(
        storyboardId, scene.scene_id, scene.approved_version_id || approveTargetId,
      );
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update approval");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`gallery-scene-${scene.scene_id}`}
      className="flex flex-col gap-2"
    >
      <button
        type="button"
        onClick={() => onOpen?.(scene.scene_id)}
        data-testid={`gallery-scene-open-${scene.scene_id}`}
        className={`group relative aspect-video w-full overflow-hidden rounded-sm border-4 ${borderColor} bg-zinc-950 hover:brightness-110 transition-all text-left`}
        title="Click to review + edit this scene"
      >
        {imgSrc ? (
          <img
            src={imgSrc}
            alt={`Scene ${idx + 1}`}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-zinc-600">
            <ImageIcon className="w-8 h-8" />
          </div>
        )}
        {/* Rendering shim */}
        {scene.preview_status === "running" && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
            <Loader2 className="w-6 h-6 text-yellow-300 animate-spin" />
          </div>
        )}
        {/* Corner badge */}
        <div
          className={`absolute top-2 left-2 font-mono text-[9px] uppercase tracking-widest border px-2 py-0.5 rounded-sm ${badge.cls}`}
        >
          {badge.label}
        </div>
        {approved && (
          <div
            aria-hidden
            className="absolute top-2 right-2 flex items-center justify-center h-6 w-6 rounded-full bg-emerald-400 text-black"
          >
            <Check className="w-4 h-4" strokeWidth={3} />
          </div>
        )}
        {/* Scene number overlay bottom-right */}
        <div className="absolute bottom-2 right-2 font-mono text-[10px] uppercase tracking-widest bg-black/70 text-yellow-400 px-2 py-0.5 rounded-sm">
          #{idx + 1}
        </div>
      </button>

      <div className="flex items-center gap-2">
        <div className="font-mono text-[11px] uppercase tracking-wider text-zinc-300 min-w-0 flex-1 truncate">
          Scene {idx + 1}
          {scene.description && (
            <span className="ml-2 text-zinc-500 normal-case tracking-normal font-sans">
              — {scene.description.slice(0, 60)}
              {scene.description.length > 60 ? "…" : ""}
            </span>
          )}
        </div>
        {!readOnly && (
          <button
            type="button"
            onClick={toggleApprove}
            disabled={!canApprove || busy}
            data-testid={`gallery-scene-approve-${scene.scene_id}`}
            title={
              !hasAnyVersion
                ? "Render a preview first, then you can approve it"
                : approved
                ? "Click to un-approve"
                : "Approve this scene"
            }
            className={`shrink-0 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-sm border transition-colors disabled:cursor-not-allowed ${
              approved
                ? "border-emerald-400 text-emerald-200 bg-emerald-500/10 hover:bg-emerald-500/20"
                : "border-yellow-400/60 text-yellow-200 hover:bg-yellow-400/10 disabled:opacity-40"
            }`}
          >
            {busy ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" />
                …
              </>
            ) : approved ? (
              <>
                <Check className="w-3 h-3" strokeWidth={3} /> approved
              </>
            ) : (
              <>✓ approve</>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

export default function SceneGalleryGrid({
  scenes,
  storyboardId,
  onOpenScene,
  onChanged,
  readOnly = false,
}) {
  if (!scenes || scenes.length === 0) {
    return (
      <div
        data-testid="gallery-empty"
        className="border border-dashed border-zinc-800 rounded-sm p-8 text-center text-sm text-zinc-500"
      >
        No scenes yet. Switch to <b className="text-yellow-400">Detail</b> view
        and add the first scene at the bottom.
      </div>
    );
  }
  return (
    <div
      data-testid="gallery-grid"
      className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
    >
      {scenes.map((scene, i) => (
        <GalleryCard
          key={scene.scene_id}
          scene={scene}
          idx={i}
          storyboardId={storyboardId}
          onOpen={onOpenScene}
          onChanged={onChanged}
          readOnly={readOnly}
        />
      ))}
    </div>
  );
}
